import is from '@sindresorhus/is';

import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import type { CommentHighlightState } from './comment-highlight';
import { useCommentsScope } from './comments-scope-context';
import type { Comment, CommentTarget } from './types';
import { useCommentsQuery } from './use-comments-query';

type CommentableTargetParams =
    | { readonly kind: 'title'; readonly itemId: string; readonly skillId?: undefined }
    | { readonly kind: 'skill'; readonly itemId: string; readonly skillId: string };

type CommentableState = {
    readonly state: CommentHighlightState;
    readonly commentId: string | undefined;
    readonly savedComment: Comment | undefined;
};

const matchesTarget = (target: CommentTarget, params: CommentableTargetParams): boolean => {
    if (params.kind === 'title') {
        return target.kind === 'title';
    }
    return target.kind === 'skill' && target.skillId === params.skillId;
};

export const useCommentableState = (params: CommentableTargetParams): CommentableState => {
    const { architectureId, itemId, language, version } = useCommentsScope();
    const { data: comments } = useCommentsQuery({ architectureId, itemId, language, version });
    const activeThreadId = useActiveThreadStore((state) => state.activeThreadId);
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);

    const savedComment = comments?.find(
        (comment) => is.nullOrUndefined(comment.resolution) && matchesTarget(comment.target, params),
    );
    const isActive = !is.undefined(savedComment) && activeThreadId === savedComment.id;
    const hasPending = !is.null(pendingTarget) && matchesTarget(pendingTarget.target, params);

    const resolveState = (): CommentHighlightState => {
        if (isActive) return 'active';
        if (!is.undefined(savedComment)) return 'saved';
        if (hasPending) return 'pending';
        return 'none';
    };

    return {
        state: resolveState(),
        commentId: savedComment?.id,
        savedComment,
    };
};
